import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API } from "../lib/api";
import { inr, clampNonNegative } from "../lib/format";
import { amountInWords } from "../lib/utils";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { printDom } from "../lib/printDom";
import { printServerPdf } from "../lib/printPdf";
import { MoneyInput } from "../components/MoneyInput";
import { toPackets, formatBoxSheetPackets } from "../lib/productUnits";
import PaymentModal from "../components/PaymentModal";
import Letterhead from "../components/Letterhead";
import { useConfirm } from "../components/ConfirmDialog";
import { useAuth } from "../lib/auth";
import { useMultiSelect } from "../lib/multiSelect";
import MultiSelectBar from "../components/MultiSelectBar";
import { toast } from "sonner";
import { Plus, Trash, Eye, CheckCircle, XCircle, Printer, WhatsappLogo, Truck, Copy } from "@phosphor-icons/react";
import { Pagination, usePagination } from "../components/Pagination";

const TITLES = {
  quotation: { label: "Quotation", subtitle: "Quote to customers with item lines and GST.", screen: "QUOTATIONS" },
  purchase_order: { label: "Purchase Order", subtitle: "Order raw materials and supplies from suppliers.", screen: "PURCHASE ORDERS" },
  purchase_invoice: { label: "Purchase Invoice", subtitle: "Bills received from your suppliers.", screen: "PURCHASE INVOICES" },
  sales_invoice: { label: "Sales Invoice (GST)", subtitle: "Tax invoices for customers with full GST breakup.", screen: "SALES INVOICES" },
};

export default function Documents({ type }) {
  const t = TITLES[type];
  const { confirm } = useConfirm();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const ms = useMultiSelect();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  // Sales-invoice payment modal — holds the linked receivable entry when the
  // operator clicks "₹ Received" on a row.
  const [paying, setPaying] = useState(null);
  const [clone, setClone] = useState(null);
  const [view, setView] = useState(null);
  const [auditOpen, setAuditOpen] = useState(false);
  const [monthlyOpen, setMonthlyOpen] = useState(false);
  const { page, setPage, pageSize, setPageSize } = usePagination(20);
  const [sortBy, setSortBy] = useState("date_desc");
  const [q, setQ] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");   // "all" | "overdue" | "received" | "pending"
  // Reset to page 1 when the filter/sort/search changes.
  useEffect(() => { setPage(1); }, [q, sortBy, statusFilter, setPage]);

  const load = async () => setItems((await api.get("/documents", { params: { doc_type: type } })).data);
  useEffect(() => { load(); }, [type]);

  // Deep-link: /sales-invoices?edit={doc_id} auto-opens the edit modal for that
  // doc once the list is loaded (used by the quotation → invoice conversion).
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const target = searchParams.get("edit");
    if (!target || !items.length) return;
    const found = items.find(d => d.id === target);
    if (!found) return;
    setEdit(found);
    const next = new URLSearchParams(searchParams);
    next.delete("edit");
    setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, searchParams]);

  // Preload the accounting ledger once so every sales-invoice row can render
  // its own "received" state without a per-row API call. Keyed by
  // `linked_doc_id → {id, status, paid_total, balance_due, amount}`.
  // Declared BEFORE `sortedItems` so the useMemo can reference it safely.
  const [receivables, setReceivables] = useState({});

  const sortedItems = React.useMemo(() => {
    const today = new Date().toISOString().slice(0, 10);
    const filtered = items.filter(d => {
      const matchesQ = !q
        || d.doc_no?.toLowerCase().includes(q.toLowerCase())
        || d.party?.name?.toLowerCase().includes(q.toLowerCase());
      if (!matchesQ) return false;
      if (type !== "sales_invoice" || statusFilter === "all") return true;
      const rc = receivables[d.id];
      const fully = rc && rc.status === "cleared";
      if (statusFilter === "received") return fully;
      if (statusFilter === "pending") return !fully;
      if (statusFilter === "overdue") {
        return !fully && d.due_date && d.due_date < today && rc;
      }
      return true;
    });
    // Doc numbers look like "SF-26-0083" — sort by the trailing integer so
    // 0083 correctly comes before 0100, not "080" > "100".
    const docNum = (s) => {
      const m = String(s || "").match(/(\d+)(?!.*\d)/);
      return m ? parseInt(m[1], 10) : 0;
    };
    const arr = [...filtered];
    arr.sort((a, b) => {
      switch (sortBy) {
        case "date_asc":    return (a.doc_date || "").localeCompare(b.doc_date || "");
        case "date_desc":   return (b.doc_date || "").localeCompare(a.doc_date || "");
        case "amount_desc": return (b.grand_total || 0) - (a.grand_total || 0);
        case "amount_asc":  return (a.grand_total || 0) - (b.grand_total || 0);
        case "party":       return (a.party?.name || "").localeCompare(b.party?.name || "");
        case "doc_no_desc": return docNum(b.doc_no) - docNum(a.doc_no);
        case "doc_no_asc":  return docNum(a.doc_no) - docNum(b.doc_no);
        default: return 0;
      }
    });
    return arr;
  }, [items, sortBy, q, type, statusFilter, receivables]);

  // Live counts per status filter so the pill badges are informative even
  // before the operator switches tabs. Only meaningful on sales_invoice.
  const statusCounts = React.useMemo(() => {
    if (type !== "sales_invoice") return null;
    const today = new Date().toISOString().slice(0, 10);
    let received = 0, pending = 0, overdue = 0;
    for (const d of items) {
      const rc = receivables[d.id];
      const fully = rc && rc.status === "cleared";
      if (fully) received += 1;
      else pending += 1;
      if (!fully && d.due_date && d.due_date < today && rc) overdue += 1;
    }
    return { all: items.length, received, pending, overdue };
  }, [items, receivables, type]);

  const del = async (doc) => {
    const ok = await confirm({
      title: "Delete document?",
      message: `${doc.doc_no} (${doc.party?.name || "—"}) will be permanently deleted. Any linked accounting entry will be revoked.`,
      tone: "danger",
      confirmLabel: "Delete Document",
    });
    if (!ok) return;
    await api.delete(`/documents/${doc.id}`);
    toast.success("Deleted");
    load();
  };

  // Populate `receivables` once items load. Uses same map-building logic as
  // above (declaration moved up to satisfy hoisting).
  useEffect(() => {
    if (type !== "sales_invoice") return;
    api.get("/accounting").then(r => {
      const map = {};
      for (const e of (r.data || [])) {
        if (e.entry_type === "receivable" && e.linked_doc_id) {
          const prev = map[e.linked_doc_id];
          if (!prev || (Number(e.amount) > Number(prev.amount))) {
            map[e.linked_doc_id] = e;
          }
        }
      }
      setReceivables(map);
    }).catch(() => {});
  }, [type, items]);


  const duplicate = (doc) => {
    // Open the create form pre-filled with the source doc's fields. User can
    // review + tweak (party, items, dates, disc %) before saving. Admin can also
    // override the auto-generated doc_no. Advance and auto-inventory reset.
    const today = new Date().toISOString().slice(0, 10);
    const seed = {
      ...doc,
      id: undefined,           // signal "new doc" to the form (isEdit=false)
      doc_no: "",              // fresh auto-number; admin can override in-form
      doc_date: today,
      due_date: today,
      advance_paid: 0,
      auto_inventory: false,
      items: (doc.items || []).map(i => ({ ...i })),
    };
    setClone(seed);
  };

  const convertToInvoice = async (quote) => {
    const ok = await confirm({
      tone: "info",
      title: `Convert ${quote.doc_no} → Sales Invoice?`,
      message:
        `A new sales invoice will be created pre-filled from this quotation. ` +
        `You'll be dropped into the invoice edit form to verify the party, ` +
        `line items, taxes and doc date before saving.\n\n` +
        `The original quotation is preserved.`,
      confirmLabel: "Convert",
      cancelLabel: "Cancel",
    });
    if (!ok) return;
    try {
      const r = await api.post(`/documents/${quote.id}/convert-to-invoice`);
      const newId = r.data?.id;
      toast.success(`Created invoice ${r.data.doc_no} — opening for review…`);
      // Jump to Sales Invoices with a deep-link that auto-opens the edit modal.
      setTimeout(() => {
        window.location.href = newId
          ? `/sales-invoices?edit=${newId}`
          : "/sales-invoices";
      }, 400);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : (d?.message || "Failed to convert"));
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label={t.screen}
        title={t.label}
        subtitle={t.subtitle}
        action={
          <div className="flex gap-2">
            {type === "sales_invoice" && (
              <Btn variant="secondary" onClick={() => setMonthlyOpen(true)} data-testid="monthly-sales-btn">
                📊 Monthly total
              </Btn>
            )}
            {isAdmin && (
              <Btn variant="secondary" onClick={() => setAuditOpen(true)} data-testid="doc-audit-btn">
                🩺 Audit accounting
              </Btn>
            )}
            <Btn onClick={() => setOpen(true)} data-testid="new-doc"><Plus size={14} /> New {t.label}</Btn>
          </div>
        }
      />

      {type === "sales_invoice" && statusCounts && (
        <div className="mb-3 flex flex-wrap gap-1.5 items-center" data-testid="doc-status-filter">
          {[
            { k: "all",      label: "All",      cls: "bg-zinc-900 text-white",   pill: "text-zinc-700" },
            { k: "overdue",  label: "Overdue",  cls: "bg-red-600 text-white",    pill: "text-red-700" },
            { k: "pending",  label: "Pending",  cls: "bg-orange-600 text-white", pill: "text-orange-700" },
            { k: "received", label: "Received", cls: "bg-green-600 text-white",  pill: "text-green-700" },
          ].map(t => {
            const on = statusFilter === t.k;
            const count = statusCounts[t.k] || 0;
            return (
              <button key={t.k} type="button" onClick={() => setStatusFilter(t.k)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono uppercase tracking-widest transition-colors ${
                  on ? t.cls : `bg-white border border-zinc-200 hover:bg-zinc-50 ${t.pill}`
                }`}
                data-testid={`doc-status-tab-${t.k}`}
              >
                <span className="font-bold">{t.label}</span>
                <span className={`text-[10px] px-1.5 py-0.5 rounded ${on ? "bg-white/25" : "bg-zinc-100 text-zinc-500"}`}>
                  {count}
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="mb-3 flex flex-wrap gap-2 items-center">
        <input
          value={q}
          onChange={e => setQ(e.target.value)}
          placeholder="Search doc no or party…"
          className="flex-1 min-w-[220px] max-w-md px-3 py-2 border border-zinc-300 dark:border-zinc-700 text-sm font-mono bg-white dark:bg-zinc-900"
          data-testid="doc-search"
        />
        <select
          value={sortBy}
          onChange={e => setSortBy(e.target.value)}
          className="px-3 py-2 border border-zinc-300 dark:border-zinc-700 text-sm bg-white dark:bg-zinc-900"
          data-testid="doc-sort"
        >
          <option value="date_desc">Sort: Date (newest)</option>
          <option value="date_asc">Sort: Date (oldest)</option>
          <option value="doc_no_desc">Sort: Doc No (high → low)</option>
          <option value="doc_no_asc">Sort: Doc No (low → high)</option>
          <option value="amount_desc">Sort: Amount (high → low)</option>
          <option value="amount_asc">Sort: Amount (low → high)</option>
          <option value="party">Sort: Party (A → Z)</option>
        </select>
        <div className="text-xs text-zinc-500 dark:text-zinc-400 font-mono">
          {sortedItems.length} of {items.length}
        </div>
      </div>

      <Card className="!p-0">
        {sortedItems.length === 0 ? (
          <div className="p-6"><EmptyState title={`No ${t.label.toLowerCase()} yet`} /></div>
        ) : (
          <>
          {(() => {
            const pagedItems = sortedItems.slice((page - 1) * pageSize, page * pageSize);
            return (
          <table className="kr-table">
            <thead>
              <tr>
                {isAdmin && (
                  <th className="w-8">
                    <input type="checkbox" checked={ms.selected.length > 0 && ms.selected.length === pagedItems.length}
                      onChange={() => ms.toggleAll(pagedItems.map(x => x.id))} className="accent-orange-600" />
                  </th>
                )}
                <th>Doc No</th><th>Date</th><th>Party</th><th>GSTIN</th><th>Items</th><th>Total</th><th></th>
              </tr>
            </thead>
            <tbody>
              {pagedItems.map(d => {
                // Compute overdue at row level so we can also tint the whole
                // row red (matches the sales team's "chase this one first" cue).
                const _rc = receivables[d.id];
                const _fullyReceived = _rc && _rc.status === "cleared";
                const _today = new Date().toISOString().slice(0, 10);
                const _isOverdue = type === "sales_invoice"
                  && !_fullyReceived && d.due_date && d.due_date < _today && _rc;
                return (
                <tr key={d.id} data-testid={`doc-row-${d.doc_no}`}
                    className={_isOverdue ? "bg-red-50/60 border-l-4 border-red-500" : ""}>
                  {isAdmin && (
                    <td>
                      <input type="checkbox" checked={ms.isSelected(d.id)} onChange={() => ms.toggle(d.id)} className="accent-orange-600" data-testid={`doc-check-${d.doc_no}`} />
                    </td>
                  )}
                  <td className="font-mono text-orange-700 font-bold">{d.doc_no}</td>
                  <td className="font-mono text-xs">{d.doc_date}</td>
                  <td className="font-bold">{d.party.name}</td>
                  <td>
                    {d.party.gstin ? (
                      <span className="font-mono text-xs flex items-center gap-1">
                        {d.party.gstin}
                        {d.gstin_status === "valid"
                          ? <CheckCircle size={14} weight="fill" className="text-green-600" />
                          : <XCircle size={14} weight="fill" className="text-red-600" />}
                      </span>
                    ) : <span className="text-zinc-400 text-xs">—</span>}
                  </td>
                  <td className="font-mono">{d.items.length}</td>
                  <td className="font-mono font-bold">{inr(d.grand_total)}</td>
                  <td className="flex gap-3">
                    <button onClick={() => setView(d)} data-testid={`view-${d.doc_no}`} className="text-orange-600" title="View"><Eye size={16} weight="bold" /></button>
                    <button onClick={() => setEdit(d)} data-testid={`edit-${d.doc_no}`} className="text-blue-600 ml-2" title="Edit"><Truck size={0} weight="bold" style={{ display: "none" }} /><span className="text-xs font-bold uppercase tracking-widest">Edit</span></button>
                    <button onClick={() => duplicate(d)} data-testid={`duplicate-${d.doc_no}`} className="text-zinc-600 hover:text-zinc-900" title="Duplicate — clone this order with today's date"><Copy size={14} weight="bold" /></button>
                    {type === "sales_invoice" && (() => {
                      const rc = receivables[d.id];
                      const fullyReceived = rc && rc.status === "cleared";
                      const paid = Number(rc?.paid_total || 0);
                      const partial = !fullyReceived && paid > 0;
                      // Overdue: invoice has a due_date, still has a positive
                      // balance, and today has moved past that date.
                      const today = new Date().toISOString().slice(0, 10);
                      const due = d.due_date;
                      const overdue = !fullyReceived && due && due < today && rc;
                      const daysLate = overdue
                        ? Math.floor((new Date(today) - new Date(due)) / 86400000)
                        : 0;
                      return (
                        <div className="flex flex-col items-start gap-0.5">
                          <button
                            onClick={() => rc && !fullyReceived && setPaying(rc)}
                            disabled={!rc || fullyReceived}
                            data-testid={`received-${d.doc_no}`}
                            className={`text-xs font-bold uppercase tracking-widest ${
                              fullyReceived
                                ? "text-zinc-400 cursor-not-allowed"
                                : "text-orange-700 hover:text-orange-900"
                            }`}
                            title={fullyReceived
                              ? "Fully received — nothing left to collect"
                              : rc ? "Mark amount received against this invoice"
                                : "No pending receivable"}
                          >
                            ₹ {fullyReceived ? "Received ✓" : "Received"}
                          </button>
                          {partial && (
                            <div className="text-[9px] font-mono text-emerald-700"
                                 data-testid={`received-partial-${d.doc_no}`}>
                              Received ₹{paid.toLocaleString("en-IN")}
                            </div>
                          )}
                          {overdue && (
                            <div
                              className="text-[9px] font-mono font-bold text-red-700 bg-red-50 border border-red-200 rounded px-1"
                              data-testid={`overdue-${d.doc_no}`}
                              title={`Due ${due} — ${daysLate}d past customer's credit window`}
                            >
                              ⚠ Overdue {daysLate}d
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    {type === "quotation" && (
                      <button onClick={() => convertToInvoice(d)} data-testid={`convert-${d.doc_no}`} className="text-green-700 hover:text-green-900 text-xs font-bold uppercase tracking-widest" title="Convert this quotation to a sales invoice">
                        → Inv
                      </button>
                    )}
                    <button onClick={() => del(d)} data-testid={`delete-${d.doc_no}`} className="text-red-600 hover:text-red-800" title="Delete"><Trash size={14} weight="bold" /></button>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
            );
          })()}
          <Pagination
            page={page}
            pageSize={pageSize}
            total={sortedItems.length}
            onPageChange={setPage}
            onPageSizeChange={setPageSize}
            testId="doc-pager"
          />
          </>
        )}
      </Card>

      {open && <DocFormModal type={type} onClose={() => setOpen(false)} onCreated={() => { setOpen(false); load(); }} />}
      {paying && <PaymentModal entry={paying} onClose={() => setPaying(null)}
                    onSaved={() => { setPaying(null); load(); toast.success("Amount received recorded"); }} />}
      {edit && <DocFormModal type={type} existing={edit} onClose={() => setEdit(null)} onCreated={() => { setEdit(null); load(); }} />}
      {clone && <DocFormModal type={type} clone={clone} onClose={() => setClone(null)} onCreated={() => { setClone(null); load(); }} />}
      {view && <DocViewModal doc={view} onClose={() => setView(null)} />}
      {auditOpen && <AuditAccountingModal docType={type} onClose={() => setAuditOpen(false)} onFixed={() => load()} />}
      {monthlyOpen && <MonthlySalesModal onClose={() => setMonthlyOpen(false)} />}
      <MultiSelectBar
        count={ms.selected.length}
        ids={ms.selected}
        resource="documents"
        label={t.label.toLowerCase() + "s"}
        onCleared={() => { ms.clear(); load(); }}
      />
    </div>
  );
}

function DocFormModal({ type, existing, clone, onClose, onCreated }) {
  const isEdit = !!existing;
  // A "clone" source acts like an initial value but the modal saves as a NEW doc.
  const seed = existing || clone || null;
  const { user } = useAuth();
  const { confirm } = useConfirm();
  const isAdmin = user?.role === "admin";
  const [party, setParty] = useState(seed?.party || { name: "", gstin: "", address: "", phone: "", email: "", state: "" });
  const [parties, setParties] = useState([]);
  const [gstinCheck, setGstinCheck] = useState(null);
  const [docNo, setDocNo] = useState(existing?.doc_no || "");   // admin-only override
  const [items, setItems] = useState(seed?.items?.map(it => ({
    description: it.description || "", hsn: it.hsn || "", qty: it.qty ?? "",
    unit: it.unit || "kg", rate: it.rate || 0, gst_rate: it.gst_rate ?? "",
    discount_percent: it.discount_percent ?? "",
    item_type: it.item_type || (type === "purchase_order" ? "raw_material" : "other"),
    raw_material_id: it.raw_material_id || "",
    rate_inclusive: !!it.rate_inclusive,
  })) || [{
    description: "", hsn: "", qty: "", unit: "kg", rate: "", gst_rate: "",
    discount_percent: "",
    item_type: type === "purchase_order" ? "raw_material" : "other",
    raw_material_id: "",
    rate_inclusive: false,
  }]);
  const [transportationCost, setTransportationCost] = useState(seed?.transportation_cost || "");
  const [rawMaterials, setRawMaterials] = useState([]);
  const [skus, setSkus] = useState([]);
  const [skuStock, setSkuStock] = useState({});   // sku_id → remaining packs
  const [itemTypes, setItemTypes] = useState([
    { value: "raw_material", label: "Raw material", creates_inventory: true },
    { value: "addon", label: "Add-on", creates_inventory: false },
    { value: "other", label: "Other", creates_inventory: false },
  ]);
  const [notes, setNotes] = useState(seed?.notes || "");
  const [docDate, setDocDate] = useState(seed?.doc_date || new Date().toISOString().slice(0, 10));
  const [pos, setPos] = useState(seed?.place_of_supply || "");
  const [advancePaid, setAdvancePaid] = useState(existing?.advance_paid || "");
  const [creditDays, setCreditDays] = useState(String(seed?.credit_days || "15"));
  const [terms, setTerms] = useState(seed?.terms_and_conditions || "");
  const [paymentTerms, setPaymentTerms] = useState(seed?.payment_terms || "");
  // Auto-create inventory bag pool: always OFF by default (user must opt in).
  const [autoInventory, setAutoInventory] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const kind = (type === "quotation" || type === "sales_invoice") ? "customer" : "supplier";
    api.get("/parties", { params: { kind } }).then(r => setParties(r.data)).catch(() => { });
    if (type === "purchase_order") {
      api.get("/raw-materials").then(r => setRawMaterials(r.data)).catch(() => { });
    }
    if (type === "sales_invoice" || type === "quotation") {
      // Load SKU list + current stock so users can pick from the drop-down.
      api.get("/products").then(r => setSkus(r.data || [])).catch(() => { });
      api.get("/inventory/stock-summary").then(r => {
        const map = {};
        (r.data?.finished_goods || []).forEach(f => { map[f.sku_id] = f.remaining; });
        setSkuStock(map);
      }).catch(() => { });
    }
    api.get("/settings").then(r => {
      const t = r.data?.line_item_types;
      if (Array.isArray(t) && t.length > 0) setItemTypes(t);
    }).catch(() => { });
  }, [type]);

  const pickParty = (pid) => {
    const p = parties.find(x => x.id === pid);
    if (p) {
      setParty({
        name: p.name, gstin: p.gstin || "", address: p.address || "",
        phone: p.phone || "", email: p.email || "", state: p.state || "",
      });
      if (p.credit_days) setCreditDays(String(p.credit_days));
      // Auto-apply the customer's saved trade discount to every line item so
      // sales invoices honour the pre-negotiated rate without re-typing it.
      // Only applies to new/empty documents; when editing an existing doc,
      // leave the operator's chosen per-line % alone.
      const defDisc = parseFloat(p.default_discount_percent);
      if (Number.isFinite(defDisc) && defDisc > 0 && !isEdit) {
        setItems(prev => prev.map(it => ({
          ...it,
          discount_percent: it.discount_percent === "" || it.discount_percent == null
            ? defDisc
            : it.discount_percent,
        })));
        toast.info(`Applied ${defDisc}% default discount from ${p.name}`);
      }
    }
  };

  // Scale a per-packet rate into the requested unit so `qty × rate` stays
  // correct regardless of what unit the operator picks in the dropdown.
  // Only applies to finished-goods lines; raw-material units aren't in the
  // packets convention so they pass through untouched.
  const _packetMultiplier = (unit) => {
    const u = (unit || "").toLowerCase();
    if (u === "sheet" || u === "sheets") return 12;
    if (u === "box" || u === "boxes") return 192;
    return 1;   // packets / packet / pack / packs / unknown → 1
  };

  const updItem = (i, k, v) => {
    const c = [...items];
    const prev = c[i];
    // Special case: unit change on a finished-goods line auto-rescales the
    // rate from the previous unit to the new one so line totals reflect the
    // customer's price per box / sheet / packet.
    if (k === "unit"
        && prev.item_type !== "raw_material"
        && prev.rate != null && prev.rate !== "") {
      const oldMul = _packetMultiplier(prev.unit);
      const newMul = _packetMultiplier(v);
      if (oldMul && newMul && oldMul !== newMul) {
        // Prefer a stored per-packet rate so packets → box → packets returns
        // to the exact original number (no rounding drift). Falls back to the
        // current rate divided by the previous multiplier the first time.
        const perPacket = Number(prev.per_packet_rate) > 0
          ? Number(prev.per_packet_rate)
          : Number(prev.rate) / oldMul;
        c[i] = {
          ...prev,
          unit: v,
          per_packet_rate: perPacket,
          rate: Number((perPacket * newMul).toFixed(2)),
        };
        setItems(c);
        return;
      }
    }
    // Manual rate edit: keep the per-packet anchor in sync so subsequent unit
    // changes stay consistent with the operator's latest number.
    if (k === "rate" && prev.item_type !== "raw_material") {
      const mul = _packetMultiplier(prev.unit);
      const r = parseFloat(v);
      c[i] = { ...prev, rate: v,
        per_packet_rate: Number.isFinite(r) && mul ? r / mul : prev.per_packet_rate };
      setItems(c);
      return;
    }
    c[i] = { ...prev, [k]: v };
    setItems(c);
  };
  const addRow = () => setItems([...items, {
    description: "", hsn: "", qty: "",
    // Sales/quotation lines start on the finished-goods convention;
    // purchase-order lines default to kg for raw materials.
    unit: type === "purchase_order" ? "kg" : "packets",
    rate: "", gst_rate: "",
    discount_percent: "",
    item_type: type === "purchase_order" ? "raw_material" : "other",
    raw_material_id: "",
    rate_inclusive: false,
  }]);
  const rmRow = (i) => setItems(items.filter((_, x) => x !== i));

  const pickRawMaterial = (idx, rmId) => {
    const rm = rawMaterials.find(r => r.id === rmId);
    if (!rm) { updItem(idx, "raw_material_id", ""); return; }
    const c = [...items];
    c[idx] = { ...c[idx], raw_material_id: rmId, description: rm.name, unit: rm.unit || "kg" };
    setItems(c);
  };

  const pickSku = (idx, skuId) => {
    const sku = skus.find(s => s.id === skuId);
    if (!sku) { updItem(idx, "sku_id", ""); return; }
    const c = [...items];
    // Default the line's unit to the SKU's saved unit (usually "packets").
    // If the line already had a different finished-goods unit, keep it and
    // scale the price up by the packet multiplier so the total makes sense.
    const targetUnit = (c[idx].unit && ["packets","sheet","box"].includes(c[idx].unit))
      ? c[idx].unit
      : (sku.unit || "packets");
    const perPacket = Number(sku.sale_price) || 0;
    const scaledRate = perPacket * _packetMultiplier(targetUnit);
    c[idx] = { ...c[idx],
      sku_id: sku.id,
      description: sku.pack_size
        ? `${sku.name}` // pack info can be derived; keep desc clean
        : sku.name,
      hsn: sku.hsn || c[idx].hsn || "",
      unit: targetUnit,
      rate: perPacket ? Number(scaledRate.toFixed(2)) : (c[idx].rate || ""),
      per_packet_rate: perPacket || undefined,
      gst_rate: sku.gst_rate ?? c[idx].gst_rate,
      // Carry the SKU's "sale price is inclusive of GST" flag onto the line so
      // the operator sees the same view they used when creating the SKU.
      rate_inclusive: !!sku.sale_price_inclusive,
    };
    setItems(c);
  };

  const verify = async () => {
    if (!party.gstin) return;
    try {
      const r = await api.get(`/gstin/verify/${party.gstin}`);
      setGstinCheck(r.data);
      if (r.data.valid_format) toast.success("GSTIN format valid");
      else toast.error("Invalid GSTIN format");
    } catch { toast.error("Verification failed"); }
  };

  // Line math: gross → per-line discount → tax. (Doc-level discount removed —
  // the totals card shows the aggregate % of line discounts instead.)
  const subtotal = items.reduce((s, i) =>
    s + (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0), 0);
  const lineDiscountTotal = items.reduce((s, i) => {
    const line = (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0);
    return s + line * ((parseFloat(i.discount_percent) || 0) / 100);
  }, 0);
  const discountedSubtotal = subtotal - lineDiscountTotal;
  const totalDiscountPct = subtotal > 0 ? (lineDiscountTotal / subtotal) * 100 : 0;
  const taxTotal = items.reduce((s, i) => {
    const line = (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0);
    const afterLine = line * (1 - (parseFloat(i.discount_percent) || 0) / 100);
    return s + afterLine * (parseFloat(i.gst_rate) || 0) / 100;
  }, 0);
  const rawGrandTotal = discountedSubtotal + taxTotal + (parseFloat(transportationCost) || 0);
  // Standard Indian invoice practice: round the final grand total to the
  // nearest rupee and surface the paise adjustment on its own line so the
  // customer can see exactly how the ₹ figure was arrived at.
  const grandTotal = Math.round(rawGrandTotal);
  const roundOff = +(grandTotal - rawGrandTotal).toFixed(2);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const body = {
        doc_type: type, party, notes, doc_date: docDate, place_of_supply: pos,
        advance_paid: parseFloat(advancePaid || 0),
        credit_days: parseInt(creditDays || 15),
        discount_percent: 0,
        terms_and_conditions: terms || "",
        payment_terms: paymentTerms || "",
        auto_inventory: type === "purchase_order" ? !!autoInventory : false,
        items: items.map(i => ({
          description: i.description, hsn: i.hsn, qty: parseFloat(i.qty) || 0,
          unit: i.unit, rate: parseFloat(i.rate) || 0, gst_rate: parseFloat(i.gst_rate) || 0,
          discount_percent: parseFloat(i.discount_percent) || 0,
          item_type: i.item_type || "other",
          raw_material_id: i.raw_material_id || null,
          sku_id: i.sku_id || null,
          rate_inclusive: !!i.rate_inclusive,
        })),
        transportation_cost: parseFloat(transportationCost) || 0,
      };
      // Admin-only manual override of the auto doc_no. Non-admins never send this.
      if (isAdmin && docNo && docNo.trim()) {
        body.doc_no = docNo.trim();
      }
      if (isEdit) {
        try {
          await api.put(`/documents/${existing.id}`, body);
          toast.success("Document updated");
        } catch (err) {
          const d = err.response?.data?.detail;
          const isMismatch = err.response?.status === 409 &&
            d && typeof d === "object" && d.code === "po_reservation_mismatch";
          if (!isMismatch) throw err;
          const rows = (d.mismatches || []).map(m =>
            `• ${m.raw_material}: reserved ${m.reserved_qty}${m.unit}` +
            ` → new ${m.new_qty}${m.unit}` +
            (m.bags_drawn_qty > 0 ? ` · bags drawn ${m.bags_drawn_qty}${m.unit}` : "") +
            (m.consumed_qty > 0 ? ` · consumed ${m.consumed_qty}${m.unit}` : "")
          ).join("\n");
          const proceed = await confirm({
            tone: "warning",
            title: "PO reservation mismatch",
            message:
              `${d.message}\n\n${rows}\n\n` +
              "Proceed anyway will save the document edits WITHOUT modifying the " +
              "already-drawn bags or pool reservations. You'll need to reconcile " +
              "inventory manually.",
            confirmLabel: "Proceed anyway",
            cancelLabel: "Cancel",
          });
          if (!proceed) { setBusy(false); return; }
          await api.put(`/documents/${existing.id}?force=true`, body);
          toast.success("Document updated (reservations left untouched)");
        }
      } else {
        await api.post("/documents", body);
        toast.success("Document created");
      }

      // After a sales invoice, surface any SKUs that just went negative so
      // the user can plan a production run. Non-blocking — the invoice is
      // already saved; this is only guidance.
      if (body.doc_type === "sales_invoice") {
        try {
          const s = await api.get("/inventory/stock-summary");
          const invNames = (body.items || [])
            .map(it => (it.description || "").trim().toLowerCase())
            .filter(Boolean);
          const shorts = (s.data.finished_goods || []).filter(f =>
            invNames.includes((f.name || "").trim().toLowerCase()) && f.remaining <= 0
              && (f.total_sold || 0) > (f.total_produced || 0)
          );
          if (shorts.length > 0) {
            toast.warning(
              `Stock went negative for ${shorts.length} SKU(s):\n` +
              shorts.map(x => `· ${x.name}: sold ${x.total_sold}, made ${x.total_produced}`).join("\n") +
              `\nOpen "Production Schedule" to plan a batch.`,
              { duration: 8000 }
            );
          }
        } catch { /* silent — advisory only */ }
      }
      onCreated();
    } catch (e) {
      const d = e.response?.data?.detail;
      const msg = typeof d === "string" ? d : (d?.message || "Failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  const modalTitle = isEdit
    ? `Edit ${TITLES[type].label} · ${existing.doc_no}`
    : (clone
        ? `Duplicate → New ${TITLES[type].label}${clone.doc_no ? ` (from ${clone.doc_no})` : ""}`
        : `New ${TITLES[type].label}`);

  return (
    <Modal open onClose={onClose} title={modalTitle} size="xl">
      <form onSubmit={submit} className="space-y-6" data-testid="doc-form">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {parties.length > 0 && (
            <Field label={`Pick ${(type === "quotation" || type === "sales_invoice") ? "customer" : "supplier"} from master`} full>
              <select
                onChange={(e) => pickParty(e.target.value)}
                className="w-full px-3 py-2 bg-white border border-zinc-300 focus:outline-none focus:border-orange-500 text-sm"
                data-testid="party-picker"
                defaultValue=""
              >
                <option value="">— Type below or pick from master —</option>
                {parties.map(p => (
                  <option key={p.id} value={p.id}>
                    {p.name}{p.gstin ? ` · ${p.gstin}` : ""}
                  </option>
                ))}
              </select>
            </Field>
          )}
          <Field label="Party Name" full><Input value={party.name} onChange={e => setParty({ ...party, name: e.target.value })} required data-testid="party-name" /></Field>
          <Field label="GSTIN">
            <div className="flex gap-1">
              <Input value={party.gstin} onChange={e => setParty({ ...party, gstin: e.target.value.toUpperCase() })}
                placeholder="22ABCDE0000F1Z5" data-testid="party-gstin" />
              <Btn type="button" variant="secondary" onClick={verify} className="!px-2 !py-1 !text-[10px]">Verify</Btn>
            </div>
            {gstinCheck && (
              <div className={`mt-1 text-[11px] font-mono ${gstinCheck.valid_format ? "text-green-700" : "text-red-700"}`}>
                {gstinCheck.valid_format ? `✓ State ${gstinCheck.state_code} · PAN ${gstinCheck.pan}` : "✗ Invalid format"}
              </div>
            )}
          </Field>
          <Field label="Phone"><Input value={party.phone} onChange={e => setParty({ ...party, phone: e.target.value })} data-testid="party-phone" /></Field>
          <Field label="Email"><Input type="email" value={party.email} onChange={e => setParty({ ...party, email: e.target.value })} data-testid="party-email" /></Field>
          <Field label="Address" full><Input value={party.address} onChange={e => setParty({ ...party, address: e.target.value })} data-testid="party-address" /></Field>
          <Field label="Party State"><Input value={party.state} onChange={e => setParty({ ...party, state: e.target.value })} placeholder="Maharashtra" data-testid="party-state" /></Field>
          <Field label="Place of Supply"><Input value={pos} onChange={e => setPos(e.target.value)} placeholder="Maharashtra" data-testid="pos" /></Field>
          <Field label="Document Date"><DatePicker value={docDate} onChange={e => setDocDate(e.target.value)} /></Field>
          {isAdmin && (
            <Field label={`${TITLES[type].label} No · admin`}>
              <Input
                value={docNo}
                onChange={e => setDocNo(e.target.value)}
                placeholder={isEdit ? existing.doc_no : "Auto (leave blank)"}
                className="font-mono"
                data-testid="doc-no-override"
              />
            </Field>
          )}
          {(type === "sales_invoice" || type === "purchase_order") && (
            <Field label="Advance Paid">
              <MoneyInput
                value={advancePaid}
                onChange={e => setAdvancePaid(e.target.value)}
                min={0}
                placeholder="₹0.00"
                className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono"
                data-testid="advance-paid"
              />
            </Field>
          )}
          <Field label="Transportation Cost">
            <MoneyInput
              value={transportationCost}
              onChange={e => setTransportationCost(e.target.value)}
              min={0}
              placeholder="₹0.00"
              className="w-full px-3 py-2 border border-zinc-300 text-sm font-mono"
              data-testid="transportation-cost"
            />
          </Field>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <div className="kr-label">Line Items</div>
            <Btn type="button" variant="secondary" onClick={addRow} data-testid="add-line"><Plus size={12} /> Row</Btn>
          </div>
          <div className="border border-zinc-200 overflow-x-auto">
            <table className="kr-table">
              <thead><tr>
                {type === "purchase_order" && <th>Type</th>}
                <th>Description</th>
                <th>HSN</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Rate</th>
                <th>Disc%</th>
                <th>GST%</th>
                <th>Line</th>
                <th></th>
              </tr></thead>
              <tbody>
                {items.map((i, idx) => {
                  const grossLine = (parseFloat(i.qty) || 0) * (parseFloat(i.rate) || 0);
                  const lineDiscPct = parseFloat(i.discount_percent) || 0;
                  const line = grossLine * (1 - lineDiscPct / 100);
                  const isRM = type === "purchase_order" && i.item_type === "raw_material";
                  const isSaleSku = (type === "sales_invoice" || type === "quotation");
                  const skuRemaining = isSaleSku && i.sku_id ? skuStock[i.sku_id] : null;
                  return (
                    <tr key={idx} data-testid={`line-${idx}`}>
                      {type === "purchase_order" && (
                        <td>
                          <select
                            className="w-28 px-1 py-1 border border-zinc-200 text-xs bg-white"
                            value={i.item_type || "raw_material"}
                            onChange={e => updItem(idx, "item_type", e.target.value)}
                            data-testid={`line-type-${idx}`}
                          >
                            {itemTypes.map(t => (
                              <option key={t.value} value={t.value}>{t.label}</option>
                            ))}
                          </select>
                        </td>
                      )}
                      <td>
                        {isSaleSku && skus.length > 0 && (
                          <select
                            className="w-full px-2 py-1 border border-emerald-300 text-xs bg-emerald-50 mb-1"
                            value={i.sku_id || ""}
                            onChange={e => pickSku(idx, e.target.value)}
                            data-testid={`line-sku-picker-${idx}`}
                          >
                            <option value="">— Pick SKU (or type manually) —</option>
                            {skus.map(s => {
                              const stock = skuStock[s.id];
                              const label = `${s.name}` +
                                (s.pack_size ? ` · ${s.pack_size}${s.pack_unit || ""}` : "") +
                                ` · ₹${s.sale_price}` +
                                (stock !== undefined ? ` · ${stock} left` : "");
                              return <option key={s.id} value={s.id}>{label}</option>;
                            })}
                          </select>
                        )}
                        {isRM && rawMaterials.length > 0 && !i.raw_material_id && (
                          <select
                            className="w-full px-2 py-1 border border-orange-300 text-xs bg-orange-50 mb-1"
                            defaultValue=""
                            onChange={e => pickRawMaterial(idx, e.target.value)}
                            data-testid={`line-rm-picker-${idx}`}
                          >
                            <option value="">— Pick raw material —</option>
                            {rawMaterials.map(rm => (
                              <option key={rm.id} value={rm.id}>{rm.name} · {rm.code}</option>
                            ))}
                          </select>
                        )}
                        <input
                          className="w-full px-2 py-1 border border-zinc-200 text-sm"
                          value={i.description}
                          onChange={e => updItem(idx, "description", e.target.value)}
                          required
                          placeholder={isRM ? "e.g. Potato" : (isSaleSku ? "SKU name" : "Item description")}
                        />
                        {isSaleSku && i.sku_id && skuRemaining !== null && (
                          <div className={`mt-1 text-[10px] font-mono ${
                            (parseFloat(i.qty) || 0) > (skuRemaining || 0)
                              ? "text-red-700 font-bold"
                              : "text-emerald-700"
                          }`}>
                            Stock: {skuRemaining} packs
                            {(parseFloat(i.qty) || 0) > (skuRemaining || 0) && (
                              <> · <span className="uppercase tracking-wider">will go negative</span></>
                            )}
                          </div>
                        )}
                        {isRM && i.raw_material_id && (
                          <button type="button" onClick={() => updItem(idx, "raw_material_id", "")} className="mt-1 text-[10px] text-zinc-500 hover:text-orange-600">
                            (change raw material)
                          </button>
                        )}
                      </td>
                      <td><input className="w-20 px-2 py-1 border border-zinc-200 text-sm font-mono" value={i.hsn} onChange={e => updItem(idx, "hsn", e.target.value)} /></td>
                      <td><input type="number" step="0.01" min="0" placeholder="1" className="w-20 px-2 py-1 border border-zinc-200 text-sm font-mono" value={i.qty} onChange={e => updItem(idx, "qty", clampNonNegative(e.target.value))} data-testid={`line-qty-${idx}`} /></td>
                      <td>
                        <select
                          className="w-24 px-1 py-1 border border-zinc-200 text-sm font-mono bg-white"
                          value={i.unit || "packets"}
                          onChange={e => updItem(idx, "unit", e.target.value)}
                          data-testid={`line-unit-${idx}`}
                        >
                          {/* Finished-goods lines pick from the packets/sheet/box
                              convention. Raw-material lines still support kg for PO. */}
                          {(type === "purchase_order" || i.item_type === "raw_material") ? (
                            <>
                              <option value="kg">kg</option>
                              <option value="g">g</option>
                              <option value="l">L</option>
                              <option value="ml">ml</option>
                              <option value="nos">nos</option>
                            </>
                          ) : (
                            <>
                              <option value="packets">packets</option>
                              <option value="sheet">sheet</option>
                              <option value="box">box</option>
                            </>
                          )}
                          {/* Preserve any legacy/custom unit already saved. */}
                          {i.unit && !["packets","sheet","box","kg","g","l","ml","nos"].includes(i.unit) && (
                            <option value={i.unit}>{i.unit}</option>
                          )}
                        </select>
                        {/* Deduction preview — shows the packet-equivalent for
                            box/sheet lines so operators see the stock impact
                            while quoting. Only for finished-goods lines. */}
                        {(() => {
                          const isFG = !(type === "purchase_order" || i.item_type === "raw_material");
                          if (!isFG) return null;
                          const packets = toPackets(i.qty, i.unit || "packets");
                          if (!packets || packets <= 0) return null;
                          const u = (i.unit || "packets").toLowerCase();
                          // For pure `packets` lines the ≈ is redundant.
                          if (u === "packets" || u === "packet") return null;
                          return (
                            <div
                              className="text-[10px] font-mono text-orange-700 mt-0.5"
                              data-testid={`line-packets-approx-${idx}`}
                              title="Packet-equivalent — this is what will be deducted from stock"
                            >
                              ≈ {Number.isInteger(packets) ? packets : packets.toFixed(2)} packets
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <div className="flex flex-col gap-0.5">
                          <MoneyInput
                            value={(() => {
                              // When the operator flagged this line as inclusive, the
                              // MoneyInput shows the GST-inclusive figure so what they
                              // TYPED = what they see. `rate` stays pre-tax internally.
                              const r = parseFloat(i.rate) || 0;
                              const g = parseFloat(i.gst_rate) || 0;
                              return i.rate_inclusive && r > 0 ? +(r * (1 + g / 100)).toFixed(4) : i.rate;
                            })()}
                            onChange={e => {
                              const typed = parseFloat(e.target.value) || 0;
                              const g = parseFloat(i.gst_rate) || 0;
                              // If inclusive → back out pre-tax; else store as-is.
                              const preTax = i.rate_inclusive && g > 0
                                ? +(typed / (1 + g / 100)).toFixed(4)
                                : typed;
                              updItem(idx, "rate", preTax);
                            }}
                            min={0}
                            placeholder="₹0.00"
                            className={`w-28 px-2 py-1 border text-sm font-mono text-right ${i.rate_inclusive ? "border-emerald-300 bg-emerald-50/40" : "border-zinc-200"}`}
                            title={i.rate_inclusive ? "This rate is GST-inclusive; pre-tax value is back-calculated on save" : "Enter pre-tax rate"}
                            data-testid={`line-rate-${idx}`}
                          />
                          <label className="flex items-center gap-1 text-[10px] font-mono text-zinc-600 cursor-pointer select-none">
                            <input
                              type="checkbox"
                              checked={!!i.rate_inclusive}
                              onChange={e => {
                                const nextInc = e.target.checked;
                                // Preserve the number the operator sees on the screen when the
                                // toggle flips — mutating `rate` in the opposite direction so
                                // the visible figure doesn't jump.
                                const g = parseFloat(i.gst_rate) || 0;
                                const currentDisplay = i.rate_inclusive && g > 0
                                  ? +(((parseFloat(i.rate) || 0) * (1 + g / 100))).toFixed(4)
                                  : (parseFloat(i.rate) || 0);
                                const nextRate = nextInc && g > 0
                                  ? +((currentDisplay) / (1 + g / 100)).toFixed(4)
                                  : currentDisplay;
                                const c = [...items];
                                c[idx] = { ...c[idx], rate_inclusive: nextInc, rate: nextRate };
                                setItems(c);
                              }}
                              className="w-3 h-3 accent-emerald-600"
                              data-testid={`line-rate-inclusive-${idx}`}
                            />
                            incl. GST
                          </label>
                        </div>
                        {/* Sanity-check hint: shows the per-packet rate so
                            operators can eyeball the box/sheet scaling. Only
                            renders on finished-goods lines with a set rate. */}
                        {i.item_type !== "raw_material" && (() => {
                          const mul = _packetMultiplier(i.unit);
                          const perPacket = Number(i.per_packet_rate) > 0
                            ? Number(i.per_packet_rate)
                            : (parseFloat(i.rate) > 0 && mul ? parseFloat(i.rate) / mul : 0);
                          if (!perPacket) return null;
                          const disp = perPacket >= 100
                            ? perPacket.toFixed(2)
                            : perPacket.toFixed(perPacket < 1 ? 4 : 2);
                          return (
                            <div className="text-[10px] font-mono text-zinc-500 mt-0.5"
                                 data-testid={`line-rate-per-packet-${idx}`}
                                 title="Per-packet rate — helps sanity-check the box/sheet scaling">
                              ≈ ₹{disp} per packet
                            </div>
                          );
                        })()}
                      </td>
                      <td>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          max="100"
                          placeholder="0"
                          className="w-16 px-2 py-1 border border-zinc-200 text-sm font-mono text-right"
                          value={i.discount_percent}
                          onChange={e => {
                            // Preserve raw string while typing so trailing dots
                            // ("20.") and decimals ("20.3") aren't chopped off.
                            const raw = e.target.value;
                            if (raw === "") { updItem(idx, "discount_percent", ""); return; }
                            const n = parseFloat(raw);
                            if (Number.isNaN(n)) { updItem(idx, "discount_percent", raw); return; }
                            if (n < 0) { updItem(idx, "discount_percent", 0); return; }
                            if (n > 100) { updItem(idx, "discount_percent", 100); return; }
                            updItem(idx, "discount_percent", raw);
                          }}
                          onBlur={e => {
                            const n = parseFloat(e.target.value);
                            if (!Number.isNaN(n)) updItem(idx, "discount_percent", n);
                          }}
                          data-testid={`line-disc-${idx}`}
                        />
                      </td>
                      <td><input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        placeholder="18"
                        className="w-16 px-2 py-1 border border-zinc-200 text-sm font-mono"
                        value={i.gst_rate}
                        onChange={e => {
                          const nextGst = e.target.value === "" ? "" : Math.max(0, Math.min(100, parseFloat(e.target.value) || 0));
                          if (i.rate_inclusive) {
                            // Keep the visible (inclusive) price stable across GST changes.
                            const oldG = parseFloat(i.gst_rate) || 0;
                            const oldR = parseFloat(i.rate) || 0;
                            const display = +(oldR * (1 + oldG / 100)).toFixed(4);
                            const newG = parseFloat(nextGst) || 0;
                            const newRate = newG > 0 ? +(display / (1 + newG / 100)).toFixed(4) : display;
                            const c = [...items];
                            c[idx] = { ...c[idx], gst_rate: nextGst, rate: newRate };
                            setItems(c);
                          } else {
                            updItem(idx, "gst_rate", nextGst);
                          }
                        }}
                        data-testid={`line-gst-${idx}`} /></td>
                      <td>
                        <MoneyInput
                          value={line || ""}
                          onChange={e => {
                            const newLine = parseFloat(e.target.value) || 0;
                            const qty = parseFloat(i.qty) || 0;
                            const discFactor = 1 - (parseFloat(i.discount_percent) || 0) / 100;
                            if (qty > 0 && discFactor > 0) {
                              updItem(idx, "rate", (newLine / (qty * discFactor)).toFixed(4));
                            }
                          }}
                          min={0}
                          placeholder="₹0.00"
                          className="w-28 px-2 py-1 border border-zinc-200 text-sm font-mono bg-orange-50/60 text-right"
                          title="Edit the line total — rate is back-calculated from qty × (1 − disc%)"
                          data-testid={`line-total-${idx}`}
                        />
                      </td>
                      <td><button type="button" onClick={() => rmRow(idx)} className="text-red-600"><Trash size={12} /></button></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="flex justify-between items-end flex-wrap gap-4">
          <Field label="Notes"><Input value={notes} onChange={e => setNotes(e.target.value)} className="!w-96 max-w-full" /></Field>
          <div className="border border-zinc-300 p-3 bg-zinc-50 text-sm font-mono w-72">
            <div className="flex justify-between"><span>Subtotal</span><span>{inr(subtotal)}</span></div>
            {lineDiscountTotal > 0 && (
              <div className="flex justify-between text-orange-700 font-bold" data-testid="total-discount-pct">
                <span>Discount {totalDiscountPct.toFixed(2)}%</span>
                <span>− {inr(lineDiscountTotal)}</span>
              </div>
            )}
            <div className="flex justify-between text-zinc-600"><span>GST</span><span>{inr(taxTotal)}</span></div>
            {parseFloat(transportationCost) > 0 && (
              <div className="flex justify-between text-zinc-600">
                <span>Transportation</span>
                <span>+ {inr(parseFloat(transportationCost))}</span>
              </div>
            )}
            {Math.abs(roundOff) >= 0.005 && (
              <div className="flex justify-between text-zinc-500 italic" data-testid="round-off">
                <span>Round Off</span>
                <span>{roundOff >= 0 ? "+" : "−"} {inr(Math.abs(roundOff))}</span>
              </div>
            )}
            <div className="flex justify-between font-bold border-t border-zinc-300 pt-1 mt-1"><span>Grand Total</span><span>{inr(grandTotal)}</span></div>
            {grandTotal > 0 && (
              <div className="text-[10px] italic text-zinc-600 mt-1 leading-snug" data-testid="grand-total-in-words">
                {amountInWords(grandTotal)}
              </div>
            )}
            {parseFloat(advancePaid || 0) > 0 && (
              <>
                <div className="flex justify-between text-green-700"><span>Advance paid</span><span>− {inr(parseFloat(advancePaid))}</span></div>
                <div className="flex justify-between font-black text-red-700 border-t border-zinc-300 pt-1 mt-1">
                  <span>Balance to pay</span>
                  <span>{inr(Math.max(0, grandTotal - parseFloat(advancePaid || 0)))}</span>
                </div>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <Field label="Terms & Conditions" full>
            <textarea
              rows={3}
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-orange-500 resize-none text-sm"
              placeholder="e.g. Delivery within 15 days · Goods once sold not returnable · Interest 18% on delayed payment"
              data-testid="doc-terms"
            />
          </Field>
          <Field label="Payment Terms / Advance Notes" full>
            <textarea
              rows={3}
              value={paymentTerms}
              onChange={(e) => setPaymentTerms(e.target.value)}
              className="w-full border border-zinc-300 px-3 py-2 focus:outline-none focus:border-orange-500 resize-none text-sm"
              placeholder="e.g. 30% advance on order, 70% before dispatch. UPI to XXX@paytm"
              data-testid="doc-payment-terms"
            />
          </Field>
        </div>

        <div className="flex justify-end gap-2">
          {type === "purchase_order" && (
            <label className="flex items-center gap-2 text-xs text-zinc-700 dark:text-zinc-300 mr-auto cursor-pointer" data-testid="auto-inventory-toggle">
              <input
                type="checkbox"
                checked={autoInventory}
                onChange={(e) => setAutoInventory(e.target.checked)}
                className="h-4 w-4 accent-orange-600"
              />
              <span>{isEdit ? "Re-generate inventory items (deletes previous pending_receipt rows)" : "Auto-create inventory items with barcodes on save"}</span>
            </label>
          )}
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" disabled={busy} data-testid="doc-submit">{busy ? "Saving…" : (isEdit ? "Update Document" : "Create Document")}</Btn>
        </div>
      </form>
    </Modal>
  );
}

function DocViewModal({ doc, onClose }) {
  const [ewayOpen, setEwayOpen] = React.useState(false);
  const [waOpen, setWaOpen] = React.useState(false);
  const canGenerateEway = doc.doc_type === "sales_invoice" || doc.doc_type === "purchase_invoice";

  return (
    <Modal open onClose={onClose} title={`${doc.doc_no} · ${doc.doc_type.replace("_", " ").toUpperCase()}`} size="lg">

      <div className="bg-white p-4 print:p-0 print:bg-white print:text-black print:block print:static print:w-full" data-testid="doc-view">
            <div className="print-body-content">
              <Letterhead
                docTitle={doc.doc_type.replace(/_/g, " ").toUpperCase()}
                docMeta={[
                  [doc.doc_type === "sales_invoice" ? "Invoice #" : doc.doc_type === "purchase_order" ? "PO #" : "Doc #", doc.doc_no],
                  ["Date", doc.doc_date],
                  ["Place of Supply", doc.place_of_supply || "—"],
                  ["Tax Type", doc.same_state ? "CGST + SGST" : "IGST"],
                ]}
                pageHead={
                  <div className="grid grid-cols-2 gap-6">
                    <div>
                      <div className="kr-label mb-1">{doc.doc_type === "purchase_order" || doc.doc_type === "purchase_invoice" || doc.doc_type === "misc_purchase" ? "Supplier" : "Billed To"}</div>
                      <div className="font-bold text-lg">{doc.party.name}</div>
                      <div className="text-sm">{doc.party.address}</div>
                      <div className="text-sm">{doc.party.state}</div>
                      {doc.party.gstin && (
                        <div className="text-sm font-mono">GSTIN: {doc.party.gstin}</div>
                      )}
                      <div className="text-sm">{doc.party.phone}</div>
                    </div>
                    <div className="text-right text-sm text-zinc-500 font-mono">
                      <div className="kr-label">Reference</div>
                      <div className="text-lg text-orange-700 font-bold mt-1">{doc.doc_no}</div>
                    </div>
                  </div>
                }
              >
                {(() => {
                  const hasAnyDisc = doc.items.some(i => (i.discount_percent || 0) > 0);
                  return (
                <table className="kr-table w-full border-collapse print:text-xs print:break-inside-auto">
                  <thead>
                    <tr>
                      <th>#</th><th>Description</th><th>HSN</th><th>Qty</th><th>Rate</th>
                      {hasAnyDisc && <th>Disc%</th>}
                      <th>GST%</th><th>Tax</th><th>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {doc.items.map((i, idx) => (
                      <tr key={idx}>
                        <td>{idx + 1}</td>
                        <td>{i.description}</td>
                        <td className="font-mono">{i.hsn}</td>
                        <td className="font-mono">{i.qty} {i.unit}</td>
                        <td className="font-mono">{inr(i.rate)}</td>
                        {hasAnyDisc && (
                          <td className="font-mono">{(i.discount_percent || 0) > 0 ? `${i.discount_percent}%` : "—"}</td>
                        )}
                        <td className="font-mono">{i.gst_rate}%</td>
                        <td className="font-mono">{inr(i.tax_amount)}</td>
                        <td className="font-mono font-bold">{inr((i.line_total + i.tax_amount))}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                  );
                })()}

                <div className="flex justify-end mt-4">
                  <div className="w-72 font-mono text-sm border border-zinc-300 p-3 bg-zinc-50 print:bg-transparent print:w-80 print:text-xs">
                    <div className="flex justify-between py-1"><span>Subtotal</span><span>{inr(doc.subtotal)}</span></div>
                    {((doc.line_discount_amount || 0) + (doc.discount_amount || 0)) > 0 && (
                      <>
                        <div className="flex justify-between py-1 text-orange-700 font-bold print:text-black">
                          <span>Discount {doc.subtotal > 0 ? (((doc.line_discount_amount || 0) + (doc.discount_amount || 0)) / doc.subtotal * 100).toFixed(2) : "0.00"}%</span>
                          <span>− {inr((doc.line_discount_amount || 0) + (doc.discount_amount || 0))}</span>
                        </div>
                        <div className="flex justify-between py-1 border-t border-zinc-300"><span>Taxable value</span><span>{inr(doc.discounted_subtotal || doc.subtotal)}</span></div>
                      </>
                    )}
                    {doc.same_state ? (
                      <>
                        <div className="flex justify-between py-1"><span>CGST</span><span>{inr(doc.cgst)}</span></div>
                        <div className="flex justify-between py-1"><span>SGST</span><span>{inr(doc.sgst)}</span></div>
                      </>
                    ) : (
                      <div className="flex justify-between py-1"><span>IGST</span><span>{inr(doc.igst)}</span></div>
                    )}
                    {(doc.transportation_cost || 0) > 0 && (
                      <div className="flex justify-between py-1"><span>Transportation</span><span>+ {inr(doc.transportation_cost)}</span></div>
                    )}
                    {Math.abs(doc.round_off || 0) >= 0.005 && (
                      <div className="flex justify-between py-1 text-zinc-500 italic print:text-zinc-700">
                        <span>Round Off</span>
                        <span>{(doc.round_off || 0) >= 0 ? "+" : "−"} {inr(Math.abs(doc.round_off || 0))}</span>
                      </div>
                    )}
                    <div className="flex justify-between py-2 border-t-2 border-zinc-900 mt-1 font-bold text-base print:text-sm">
                      <span>GRAND TOTAL</span><span>{inr(doc.grand_total)}</span>
                    </div>
                    {(doc.grand_total || 0) > 0 && (
                      <div className="text-[11px] italic text-zinc-600 mt-1 leading-snug print:text-zinc-800">
                        {amountInWords(doc.grand_total)}
                      </div>
                    )}
                    {(doc.advance_paid || 0) > 0 && (
                      <>
                        <div className="flex justify-between py-1 text-green-700 print:text-black"><span>Advance paid</span><span>− {inr(doc.advance_paid)}</span></div>
                        <div className="flex justify-between py-2 border-t border-red-500 mt-1 font-black text-red-700 text-base print:text-black print:text-sm">
                          <span>BALANCE TO PAY</span>
                          <span>{inr(Math.max(0, doc.grand_total - (doc.advance_paid || 0)))}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {doc.notes && <div className="mt-4 text-xs text-zinc-600 mb-4"><span className="kr-label">Notes:</span> {doc.notes}</div>}

                {/* T&C and Payment Terms — MUST stay inside Letterhead so
                    printDom's .lh-body extraction picks them up and they
                    print at the end of the document (below the totals). */}
                {(doc.terms_and_conditions || doc.payment_terms) && (
                  <div className="pt-3 mt-3 border-t border-dashed border-zinc-300">
                    {doc.terms_and_conditions && (
                      <div className="mb-3">
                        <div className="kr-label mb-1">Terms &amp; Conditions</div>
                        <div className="text-xs whitespace-pre-line text-zinc-700">{doc.terms_and_conditions}</div>
                      </div>
                    )}
                    {doc.payment_terms && (
                      <div>
                        <div className="kr-label mb-1">Payment Terms</div>
                        <div className="text-xs whitespace-pre-line text-zinc-700">{doc.payment_terms}</div>
                      </div>
                    )}
                  </div>
                )}
              </Letterhead>
            </div>

        {/* Action controls explicitly hidden via print:hidden styling */}
        <div className="flex justify-end gap-2 mt-6 print:hidden">
          <Btn variant="secondary" onClick={() => setWaOpen(true)} data-testid="wa-doc"><WhatsappLogo size={14} weight="bold" /> WhatsApp</Btn>
          <Btn variant="secondary" onClick={() => printDom('[data-testid="doc-view"]', `${doc.doc_type.replace(/_/g, " ")} ${doc.doc_no}`)} data-testid="print-doc"><Printer size={14} /> Print</Btn>
          {canGenerateEway && (
            <Btn variant="secondary" onClick={() => setEwayOpen(true)} data-testid="eway-btn">
              <Truck size={14} weight="bold" /> E-Way Bill
            </Btn>
          )}
          <Btn onClick={onClose}>Close</Btn>
        </div>
      </div>
      {ewayOpen && <EwayBillModal doc={doc} onClose={() => setEwayOpen(false)} />}
      {waOpen && <WhatsAppConfirmModal doc={doc} onClose={() => setWaOpen(false)} />}
    </Modal>
  );
}

/**
 * Confirm-the-phone-number popup shown before opening WhatsApp. The vendor's
 * saved number is pre-filled and editable — user can correct it in place
 * before we deep-link into wa.me.
 */
function WhatsAppConfirmModal({ doc, onClose }) {
  const initial = React.useMemo(() => {
    const raw = (doc.party?.phone || "").replace(/[^0-9]/g, "");
    return raw.length === 10 ? `+91 ${raw}` : (raw ? `+${raw}` : "");
  }, [doc.party?.phone]);
  const [phone, setPhone] = React.useState(initial);
  const send = () => {
    let digits = phone.replace(/[^0-9]/g, "");
    if (!digits) { toast.error("Enter a phone number"); return; }
    if (digits.length === 10) digits = "91" + digits;
    if (digits.length < 10) { toast.error("Number looks incomplete"); return; }
    const msg = `Hi ${doc.party.name},\n\nHere are the details of *${doc.doc_type.replace(/_/g, " ").toUpperCase()} ${doc.doc_no}* dated ${doc.doc_date}.\n\nTotal: ₹${doc.grand_total.toLocaleString("en-IN")}\n\nKindly review.\n— Sneha Foods (EATH'S)`;
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(msg)}`;
    const win = window.open(url, "_blank", "noopener,noreferrer");
    if (!win) window.location.href = url;
    onClose();
  };
  return (
    <Modal open onClose={onClose} title="Confirm WhatsApp number" size="sm">
      <div className="space-y-4" data-testid="wa-confirm-modal">
        <div>
          <div className="kr-label mb-1">Recipient</div>
          <div className="font-bold">{doc.party?.name || "—"}</div>
          <div className="text-xs text-zinc-500 font-mono">{doc.doc_no} · ₹{Number(doc.grand_total || 0).toLocaleString("en-IN")}</div>
        </div>
        <Field label="WhatsApp number (with country code)">
          <Input
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+91 98765 43210"
            data-testid="wa-phone-input"
            autoFocus
          />
        </Field>
        <div className="text-[11px] text-zinc-500 leading-relaxed">
          The message will open in WhatsApp with the invoice summary pre-filled.
          You'll need to attach the PDF separately after the chat opens.
        </div>
        <div className="flex gap-2 justify-end pt-2">
          <Btn variant="secondary" onClick={onClose} data-testid="wa-cancel">Cancel</Btn>
          <Btn onClick={send} data-testid="wa-send">
            <WhatsappLogo size={14} weight="bold" /> Open WhatsApp
          </Btn>
        </div>
      </div>
    </Modal>
  );
}

function EwayBillModal({ doc, onClose }) {
  const [data, setData] = React.useState(null);
  const [form, setForm] = React.useState({
    transporter_name: doc.transporter_name || "",
    transporter_id: doc.transporter_id || "",
    vehicle_no: doc.vehicle_no || "",
    transport_mode: doc.transport_mode || "road",
    distance_km: doc.distance_km || 0,
    dispatch_from_address: doc.dispatch_from_address || "",
    ship_to_address: doc.ship_to_address || doc.party?.address || "",
    eway_bill_no: doc.eway_bill_no || "",
    eway_bill_date: doc.eway_bill_date || "",
  });
  const [busy, setBusy] = React.useState(false);
  const [step, setStep] = React.useState(form.vehicle_no && form.distance_km > 0 ? "preview" : "fill");

  const load = React.useCallback(async () => {
    const r = await api.get(`/documents/${doc.id}/eway-bill`);
    setData(r.data);
  }, [doc.id]);
  React.useEffect(() => { load(); }, [load]);

  const upd = (k, v) => setForm({ ...form, [k]: v });
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/documents/${doc.id}/eway-bill`, {
        ...form,
        distance_km: parseFloat(form.distance_km || 0),
      });
      await load();
      setStep("preview");
      toast.success("E-way bill details saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const downloadJson = () => {
    if (!data?.eway_json) return;
    const blob = new Blob([JSON.stringify(data.eway_json, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `eway-bill-${doc.doc_no}.json`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast.success("E-way bill JSON downloaded — upload to GSTN portal");
  };

  const readable = data?.readable;
  return (
    <Modal open onClose={onClose} title={`E-Way Bill · ${doc.doc_no}`} size="lg">
      <div data-testid="eway-modal">
        {step === "fill" && (
          <form onSubmit={(e) => { e.preventDefault(); save(); }} className="grid grid-cols-2 gap-4">
            {readable && readable.eligible === false && (
              <div className="col-span-2 bg-yellow-50 border-l-4 border-yellow-500 p-3 text-xs">
                <b>Note:</b> Invoice total {inr(readable.totals.grand_total)} is below the ₹50,000 threshold — e-way bill is generally not mandatory. You can still generate one if the goods movement requires it.
              </div>
            )}
            <Field label="Transporter Name">
              <Input value={form.transporter_name} onChange={e => upd("transporter_name", e.target.value)} placeholder="ABC Logistics" data-testid="eway-transporter-name" />
            </Field>
            <Field label="Transporter ID (GSTIN or 15-char)">
              <Input value={form.transporter_id} onChange={e => upd("transporter_id", e.target.value.toUpperCase())} placeholder="Optional if self-transport" data-testid="eway-transporter-id" />
            </Field>
            <Field label="Vehicle No">
              <Input value={form.vehicle_no} onChange={e => upd("vehicle_no", e.target.value.toUpperCase())} placeholder="AP31AB1234" required data-testid="eway-vehicle" />
            </Field>
            <Field label="Transport Mode">
              <Select value={form.transport_mode} onChange={e => upd("transport_mode", e.target.value)} data-testid="eway-mode">
                <option value="road">Road</option>
                <option value="rail">Rail</option>
                <option value="air">Air</option>
                <option value="ship">Ship</option>
              </Select>
            </Field>
            <Field label="Distance (km)">
              <Input type="number" min="0" value={form.distance_km} onChange={e => upd("distance_km", e.target.value)} placeholder="Approx km" required data-testid="eway-distance" />
            </Field>
            <Field label="Existing e-Way Bill No (if any)">
              <Input value={form.eway_bill_no} onChange={e => upd("eway_bill_no", e.target.value)} placeholder="Auto-populated after GSTN filing" />
            </Field>
            <Field label="Dispatch From (address)" full>
              <Input value={form.dispatch_from_address} onChange={e => upd("dispatch_from_address", e.target.value)} placeholder="Leave blank to use business address" />
            </Field>
            <Field label="Ship To (address)" full>
              <Input value={form.ship_to_address} onChange={e => upd("ship_to_address", e.target.value)} placeholder="Recipient delivery address" />
            </Field>
            <div className="col-span-2 flex justify-end gap-2 pt-2">
              <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
              <Btn type="submit" disabled={busy} data-testid="eway-save">{busy ? "Saving…" : "Save & Preview"}</Btn>
            </div>
          </form>
        )}

        {step === "preview" && readable && (
          <div>
            <div className="p-4 bg-orange-50 border-l-4 border-orange-500 mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-orange-700">E-Way Bill for GSTN portal</div>
              <div className="font-black text-lg">{doc.doc_no} · {inr(readable.totals.grand_total)}</div>
              <div className="text-xs text-zinc-600">{readable.same_state ? "Intra-state (CGST + SGST)" : "Inter-state (IGST)"} · {readable.distance_km} km · {readable.transport_mode.toUpperCase()}</div>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
              <div className="border border-zinc-300 p-3 bg-zinc-50">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">From (Consignor)</div>
                <div className="font-bold">{readable.supplier.name}</div>
                <div className="text-xs">{readable.supplier.address}</div>
                <div className="text-xs">{readable.supplier.state}</div>
                <div className="text-xs font-mono">GSTIN: {readable.supplier.gstin || "URP"}</div>
              </div>
              <div className="border border-zinc-300 p-3 bg-zinc-50">
                <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-1">To (Consignee)</div>
                <div className="font-bold">{readable.recipient.name}</div>
                <div className="text-xs">{readable.recipient.address}</div>
                <div className="text-xs">{readable.recipient.state}</div>
                <div className="text-xs font-mono">GSTIN: {readable.recipient.gstin || "URP"}</div>
              </div>
            </div>

            <div className="border border-zinc-300 p-3 bg-zinc-50 mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Transport Details</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div><b>Transporter:</b> {readable.transporter_name || "—"}</div>
                <div><b>Transporter ID:</b> <span className="font-mono">{readable.transporter_id || "—"}</span></div>
                <div><b>Vehicle:</b> <span className="font-mono">{readable.vehicle_no || "—"}</span></div>
                <div><b>Mode:</b> {readable.transport_mode}</div>
                <div><b>Distance:</b> {readable.distance_km} km</div>
                {readable.eway_bill_no && <div><b>EWB No:</b> <span className="font-mono">{readable.eway_bill_no}</span></div>}
              </div>
            </div>

            <div className="border border-zinc-300 p-3 bg-zinc-50 mb-4">
              <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">Items ({data.items.length})</div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-zinc-300">
                    <th className="text-left py-1">Product</th>
                    <th className="text-left py-1">HSN</th>
                    <th className="text-right py-1">Qty</th>
                    <th className="text-right py-1">Taxable ₹</th>
                    <th className="text-right py-1">GST%</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map((it, i) => (
                    <tr key={i} className="border-b border-zinc-200">
                      <td className="py-1">{it.productName}</td>
                      <td className="py-1 font-mono">{it.hsnCode || "-"}</td>
                      <td className="py-1 text-right font-mono">{it.quantity} {it.qtyUnit}</td>
                      <td className="py-1 text-right font-mono">{inr(it.taxableAmount)}</td>
                      <td className="py-1 text-right font-mono">{(it.cgstRate + it.sgstRate + it.igstRate)}%</td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="mt-2 flex justify-between font-bold text-sm border-t border-zinc-300 pt-2">
                <span>Grand Total (incl. taxes)</span>
                <span className="font-mono">{inr(readable.totals.grand_total)}</span>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Btn type="button" variant="secondary" onClick={() => setStep("fill")}>← Edit</Btn>
              <Btn type="button" variant="secondary" onClick={() => printDom('[data-testid="eway-modal"]', `E-Way Bill ${doc.doc_no}`)}>
                <Printer size={14} weight="bold" /> Print
              </Btn>
              <Btn type="button" onClick={downloadJson} data-testid="eway-dl-json">
                <DownloadSimpleIcon /> Download JSON for GSTN
              </Btn>
              <Btn type="button" variant="secondary" onClick={onClose}>Close</Btn>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}

function DownloadSimpleIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 256 256" fill="currentColor" style={{ display: "inline-block", marginRight: 4 }}>
      <path d="M74 88h32v56H64l64 72 64-72h-42V88h32L128 16z" />
      <path d="M32 208h192v24H32z" />
    </svg>
  );
}

// ------------------------------------- Accounting audit / heal modal (admin)
const PROBLEM_LABEL = {
  missing_pending:   "No accounting entry — should be in 'To pay/receive'",
  amount_mismatch:   "Entry amount doesn't match doc grand-total",
  missing_advance:   "Advance paid but no cleared advance entry",
  orphan_advance:    "Doc has no advance but a leftover advance entry exists",
};

function AuditAccountingModal({ docType, onClose, onFixed }) {
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const run = async (fix) => {
    setBusy(true);
    try {
      const url = `/accounting/audit-documents?doc_type=${docType}${fix ? "&fix=true" : ""}`;
      const r = await api.post(url);
      setReport(r.data);
      if (fix) {
        toast.success(`Healed ${r.data.totals.fixed_this_run} entry(ies)`);
        onFixed?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Audit failed");
    } finally { setBusy(false); }
  };

  React.useEffect(() => { run(false); /* eslint-disable-next-line */ }, []);

  return (
    <Modal open title="Accounting audit" onClose={onClose} size="lg">
      <div data-testid="doc-audit-modal" className="space-y-4">
        <div className="bg-orange-50 border-l-4 border-orange-500 p-3 text-xs">
          <div className="font-bold uppercase tracking-widest text-[10px] mb-1">What this does</div>
          <div>
            Scans every {docType.replace("_", " ")} and compares it against Accounting.
            Reports any doc that's missing an entry (won't show in "To pay" / "Paid"),
            has a mismatched amount, or has orphaned advance legs. Click <strong>Heal all</strong> to auto-fix.
          </div>
        </div>

        {busy && !report && <div className="text-sm text-zinc-500 font-mono">Scanning…</div>}

        {report && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center text-xs">
              <div className="border border-zinc-300 p-2">
                <div className="kr-label">Scanned</div>
                <div className="font-mono font-black text-xl">{report.scanned}</div>
              </div>
              {Object.entries(PROBLEM_LABEL).map(([k, _]) => (
                <div key={k} className={`border-2 p-2 ${report.totals[k] > 0 ? "border-red-500 bg-red-50" : "border-zinc-300"}`}>
                  <div className="kr-label">{k.replace("_", " ")}</div>
                  <div className="font-mono font-black text-xl">{report.totals[k]}</div>
                </div>
              ))}
            </div>

            {report.issues.length === 0 ? (
              <div className="bg-green-50 border-l-4 border-green-600 p-3 text-sm font-mono">
                ✓ All documents in accounting are healthy — nothing to fix.
              </div>
            ) : (
              <div className="border border-zinc-200 max-h-[380px] overflow-y-auto">
                <table className="kr-table w-full text-xs">
                  <thead className="sticky top-0 bg-white z-10">
                    <tr>
                      <th>Doc No</th>
                      <th>Party</th>
                      <th>Grand</th>
                      <th>Advance</th>
                      <th>Problem</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.issues.map(i => (
                      <tr key={i.doc_id} data-testid={`audit-row-${i.doc_no}`}>
                        <td className="font-mono font-bold text-orange-700">{i.doc_no}</td>
                        <td className="truncate max-w-[180px]" title={i.party}>{i.party}</td>
                        <td className="font-mono">{inr(i.grand_total)}</td>
                        <td className="font-mono">{inr(i.advance_paid)}</td>
                        <td>
                          {i.problems.map(p => (
                            <div key={p} className="text-red-700">{PROBLEM_LABEL[p] || p}</div>
                          ))}
                        </td>
                        <td>
                          {i.fixed ? (
                            <span className="text-green-700 font-bold">Healed</span>
                          ) : (
                            <span className="text-orange-700">Needs fix</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Btn variant="secondary" onClick={onClose}>Close</Btn>
              <Btn variant="secondary" onClick={() => run(false)} disabled={busy} data-testid="audit-rerun">
                {busy ? "Scanning…" : "Rescan"}
              </Btn>
              {report.issues.some(i => !i.fixed) && (
                <Btn onClick={() => run(true)} disabled={busy} data-testid="audit-heal-all">
                  {busy ? "Healing…" : `Heal all (${report.issues.filter(i => !i.fixed).length})`}
                </Btn>
              )}
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

// --------------------------------------------------- Monthly sales summary
function MonthlySalesModal({ onClose }) {
  const now = new Date();
  const [year, setYear] = React.useState(now.getFullYear());
  const [month, setMonth] = React.useState(now.getMonth() + 1);
  const [data, setData] = React.useState(null);
  const [busy, setBusy] = React.useState(false);

  const load = React.useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get(`/reports/monthly-sales?year=${year}&month=${month}`);
      setData(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load");
    } finally { setBusy(false); }
  }, [year, month]);
  React.useEffect(() => { load(); }, [load]);

  const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
  return (
    <Modal open title="Monthly Sales Summary" onClose={onClose} size="lg">
      <div className="space-y-4" data-testid="monthly-sales-modal">
        <div className="flex gap-2 items-end">
          <Field label="Year">
            <select className="border border-zinc-300 px-3 py-2 text-sm font-mono"
                    value={year} onChange={e => setYear(parseInt(e.target.value))}
                    data-testid="ms-year">
              {[now.getFullYear(), now.getFullYear() - 1, now.getFullYear() - 2].map(y =>
                <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
          <Field label="Month">
            <select className="border border-zinc-300 px-3 py-2 text-sm font-mono"
                    value={month} onChange={e => setMonth(parseInt(e.target.value))}
                    data-testid="ms-month">
              {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </Field>
          <Btn variant="secondary" onClick={load} disabled={busy}>
            {busy ? "Loading…" : "Refresh"}
          </Btn>
        </div>

        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
              <div className="border-2 border-orange-500 bg-orange-50 p-3">
                <div className="kr-label">Grand total</div>
                <div className="font-mono font-black text-2xl text-orange-700" data-testid="ms-grand">
                  {inr(data.total_grand)}
                </div>
              </div>
              <div className="border border-zinc-300 p-3">
                <div className="kr-label">Invoices</div>
                <div className="font-mono font-black text-2xl">{data.invoice_count}</div>
              </div>
              <div className="border border-zinc-300 p-3">
                <div className="kr-label">Taxable</div>
                <div className="font-mono text-lg">{inr(data.total_taxable)}</div>
              </div>
              <div className="border border-zinc-300 p-3">
                <div className="kr-label">GST</div>
                <div className="font-mono text-lg">{inr(data.total_tax)}</div>
              </div>
            </div>
            <div className="border border-zinc-200 max-h-[350px] overflow-y-auto">
              <table className="kr-table w-full text-xs">
                <thead className="sticky top-0 bg-white z-10">
                  <tr><th>Date</th><th>Doc No</th><th>Party</th><th className="text-right">Total</th></tr>
                </thead>
                <tbody>
                  {data.invoices.length === 0 && (
                    <tr><td colSpan={4} className="text-center text-zinc-500 py-4">No invoices in {MONTHS[month - 1]} {year}.</td></tr>
                  )}
                  {data.invoices.map(inv => (
                    <tr key={inv.id}>
                      <td className="font-mono">{inv.doc_date}</td>
                      <td className="font-mono font-bold text-orange-700">{inv.doc_no}</td>
                      <td>{inv.party}</td>
                      <td className="text-right font-mono">{inr(inv.grand_total)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
}

